import _ from "lodash"
import { DarkMapType, BlueMapType, DarkGrayMapType, ShadesOfGrayMapType, SimplyDarkMapType, LightMapType } from "./MapTypes"
import CompanyBase from './CompanyBase'
import Trackable from './Trackable'
import StaticPoint from './StaticPoint'

import CustomMarker from './CustomMarker'
import ActivityArea from "./ActivityArea"

const EventEmitter = require( 'events' )

export default class Map extends EventEmitter {

    constructor(){
        super()
        this._map = null
        this._companyBase = null
        this._activityArea = null
        this._trackables = []
        this._staticPoints = []
        this._timeInterval = setInterval(() => {
            this.update()
        }, 1000)
    }

    /**
     * Get map
     */
    getMap(){
        return this._map
    }

    /**
     * Set the map instance in the given element
     * @param {Element} el 
     */
    setMap(el, options, mapType = "dark"){
        this._map = new google.maps.Map(el, {
            center: {lat: -23.41367408, lng: -51.90355003},
            backgroundColor: '#2A2B33',
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            mapTypeControlOptions: {
                mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain', 'light', 'dark']
            },
            zoom: 10,
            ...options
        })

        this._remount()
        this.setMapType(mapType)
    }

    /**
     * Set map theme
     * @param {*} id 
     * @param {*} mapType 
     */
    setMapType(id){
        const mapTypeMapping = {
            "dark": DarkMapType,
            "blue": BlueMapType,
            "light": LightMapType,
            "dark-grey": DarkGrayMapType,
            "shades-of-gray": ShadesOfGrayMapType,
            "simply-dark": SimplyDarkMapType
        }
        this._map.mapTypes.set(id,mapTypeMapping[id])
        this._map.setMapTypeId(id)
    }

    panTo(lat,lng){
        if(!this._map) return
        this._map.panTo(new google.maps.LatLng(lat,lng))
    }

    setActivityArea(paths, options = {}) {
        if(!this._map) return
        if(this._activityArea) 
            this._activityArea.setMap(null)
            
        const activityArea = new ActivityArea(paths, options)
        activityArea.setMap(this._map)
        return this._activityArea = activityArea.get()
    }

    get activityArea() {
        return this._activityArea
    }

    /**
     * Set a company base
     * @param {*} id 
     * @param {*} options 
     */
    setCompanyBase(id, options = {}){
        if(!this._map) return
        if(this._companyBase){
            this._companyBase.setMap(this._map)
            return this._companyBase
        }
        const companyBase = new CompanyBase(id, options)
        companyBase.setMap(this._map)
        this._companyBase = companyBase
        return this._companyBase
    }

    getCompanyBase() {
        return this._companyBase
    }

    /**
     * Get all trackables
     */
    getTrackables(){
        return this._trackables
    }

    /**
     * Find a trackble by its id
     * @param {Trackable} id 
     */
    findTrackable(id){
        return _.find(this._trackables,(trackable) => {
            return trackable.getData().id === id
        })
    }

    /**
     * Add a trackable
     * @param {*} data 
     * @param {*} options 
     */
    addTrackable(data, options = {}){
        let _data = JSON.parse(JSON.stringify(data))
        let visible = !options.hasOwnProperty('visible') ? _data.available : options.visible
        _data['visible'] = visible
        
        delete _data['user']
        if(!this._map) return
        let trackable = this.findTrackable(_data.id)
        if(trackable) return trackable
        trackable = new Trackable(options)
        let locations = []
        if(_data.user)
            delete _data['user']
        if(_data.locations){
            locations = _data.locations
            delete _data['locations']
        }
        trackable.on('location', (latLng) => {
            this.emit("location",latLng)
        })
        trackable.setData(_data)
        trackable.setMap(this._map)
        trackable.addLocations(locations)
        trackable.setVisible(visible)
        this._trackables.push(trackable)
        return trackable
    }

    /**
     * Get all static points
     */
    getStaticPoints(){
        return this._staticPoints
    }

    /**
     * Find a static point by its type and id
     * @param {*} type 
     * @param {*} id 
     */
    findStaticPoint(type,id){
        return _.find(this._staticPoints,(staticPoint) => {
            return staticPoint.getType() === type && staticPoint.getId() === id
        })
    }

    /**
     * Add a static point
     * @param {*} type 
     * @param {*} id 
     * @param {*} data 
     * @param {*} lat 
     * @param {*} lng 
     */
    addStaticPoint(type, id, data, lat = 0, lng = 0){
        const vm = this
        if(!this._map) return
        let staticPoint = this.findStaticPoint(type,id)
        
        data = JSON.parse(JSON.stringify(data))
        if(staticPoint) {
            staticPoint.setData(data)
            staticPoint.setPosition(lat,lng)
            return staticPoint
        }
        staticPoint = new StaticPoint(type,id,data)

        staticPoint.on('remove', (staticPointId) => {
            this._staticPoints = _.filter(this._staticPoints, (staticPoint) => {
                return staticPoint.getId() !== staticPointId
            })
        })

        staticPoint.setPosition(lat,lng)
        staticPoint.setMap(this._map)

        this._staticPoints.push(staticPoint)
        return staticPoint
    }

    /**
     * Happens every second
     */
    update(){
        if(this._companyBase) this._companyBase.update()
        this._trackables.forEach(trackable => trackable.update())
        this._staticPoints.forEach(staticPoint => staticPoint.update())
    }

    /**
     * Destroy class events
     */
    destroy(){
        const vm = this
        if(vm._timeInterval){
            clearInterval(vm._timeInterval)
        }
        vm._trackables = []
        vm._staticPoints = []
        vm._companyBase = null
    }

    _remount(){
        const vm = this
        this._trackables.forEach(trackable => {
            trackable.getMarker().setMap(vm._map)
            trackable.getTrack().setMap(vm._map)
        })
        this._staticPoints.forEach(staticPoint => {
            staticPoint.getMarker().setMap(vm._map)
        })
    }

}